import React, { FC, useState, useCallback, useEffect } from 'react'
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl'
import msk from 'msk'
import { Button, csx } from '@vtex/admin-ui'

import InputButton from '@/checkout-instore-legacy/commons/components/InputButton'
import {
  POSTAL_CODE_REGEX_BR,
  POSTAL_CODE_MASK_BR,
} from '@/checkout-instore-legacy/shipping/constants'
import { getAddressByPostalcode } from '@/checkout-instore-legacy/shipping/services'
import {
  withMediaQuery,
  isMobileBreakpoint,
  BreakpointType,
} from '@/checkout-instore-legacy/utils/mediaQuery'
import { genericImportantEvent } from '@/checkout-instore-legacy/utils/EventHelper'

const messages = defineMessages({
  invalidPostalCode: {
    id: 'location.modal.invalidPostalCode',
    defaultMessage: 'Invalid postal code',
  },
  postalCodeSubmit: {
    id: 'location.modal.postalCodeSubmit',
    defaultMessage: 'Submit',
  },
  emptyField: {
    id: 'location.modal.emptyField',
    defaultMessage: 'Submit',
  },
})

export interface CustomButtonProps {
  styles: any
  title: string
}

export interface PostalCodeInputProps {
  countryCode: string
  onSubmit: (address: Address) => void
  intl: IntlObject
  initialValue?: string
  breakpoint?: BreakpointType
  label?: string
  disabled?: boolean
  submitOnBlur?: boolean
  hasCustomButton?: boolean
  customButtonProps?: CustomButtonProps
  customMessage?: any
}

const PostalCodeInput: FC<PostalCodeInputProps> = ({
  countryCode,
  intl: { formatMessage },
  breakpoint,
  disabled,
  submitOnBlur,
  initialValue,
  label,
  onSubmit,
  hasCustomButton = false,
  customButtonProps,
  customMessage,
}: PostalCodeInputProps) => {
  const initialValueStr = initialValue || ''
  const isBrazilianAccount = countryCode === 'BRA'
  const postalCodeMask = isBrazilianAccount ? POSTAL_CODE_MASK_BR : ''
  const postalCodeRegex = isBrazilianAccount ? POSTAL_CODE_REGEX_BR : null

  const getMaskedValue = useCallback(
    (value: string) => msk.fit(value, postalCodeMask),
    [postalCodeMask]
  )

  const [value, setValue] = useState(
    postalCodeMask ? getMaskedValue(initialValueStr) : initialValueStr
  )
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isMobile = isMobileBreakpoint(breakpoint || undefined)

  const isValidAddress = (address: Address) =>
    !!(address.postalCode && address.country && address.state && address.city)

  const onClickAddress = useCallback(
    async (event) => {
      event && event.preventDefault()

      if (disabled) {
        return
      }

      try {
        setErrorMessage(null)
        setLoading(true)

        const address = await getAddressByPostalcode(value, countryCode)

        if (address && isValidAddress(address)) {
          onSubmit(address)
        } else {
          setErrorMessage(formatMessage(messages.invalidPostalCode))
        }
      } catch (error) {
        genericImportantEvent('location-search-postal-code', {
          description: 'Error while searching address data by postal code',
          postalCode: value,
          error: error.toString(),
        })
        setErrorMessage(formatMessage(messages.invalidPostalCode))
      }

      setLoading(false)
    },
    [countryCode, formatMessage, disabled, onSubmit, value]
  )

  const invalidPostalCode = postalCodeRegex
    ? !postalCodeRegex.test(value)
    : value.length === 0

  const handleChange = useCallback(
    (event: InputValueEvent) => {
      const { value } = event.target
      if (!invalidPostalCode) {
        errorMessage && setErrorMessage(null)
      }

      if (postalCodeMask) {
        setValue(getMaskedValue(value))
      } else {
        setValue(value)
      }
    },
    [errorMessage, postalCodeMask, setValue, getMaskedValue, invalidPostalCode]
  )

  useEffect(
    function changeErrorMessage() {
      if (!invalidPostalCode) {
        errorMessage && setErrorMessage(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invalidPostalCode]
  )

  useEffect(
    function reRenderIfInitialValueChanged() {
      if (initialValueStr) {
        setValue(
          postalCodeMask ? getMaskedValue(initialValueStr) : initialValueStr
        )
      }
    },
    [initialValueStr, postalCodeMask, setValue, getMaskedValue]
  )

  const handleBlur = useCallback(() => {
    if (invalidPostalCode) {
      const invalidField =
        value.length === 0
          ? formatMessage(messages.emptyField)
          : formatMessage(messages.invalidPostalCode)
      setErrorMessage(invalidField)
    }
    if (!invalidPostalCode && submitOnBlur) {
      onClickAddress(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidPostalCode, submitOnBlur, onClickAddress])

  return (
    <form data-cy="postalcode-form" onSubmit={onClickAddress}>
      <InputButton
        buttonProps={{
          disabled: disabled || invalidPostalCode,
          isLoading: loading,
          type: 'submit',
          text: formatMessage(messages.postalCodeSubmit),
          size: 'small',
          testId: 'postalcode-submit',
          onClick: onClickAddress,
          hasCustomButton,
        }}
        id="postalCode"
        label={label}
        disabled={!!(loading || disabled)}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={isBrazilianAccount ? '00000-000' : ''}
        size={isMobile ? 'regular' : 'large'}
        value={value}
        errorMessage={errorMessage || undefined}
        inputMode={isBrazilianAccount ? 'numeric' : undefined}
        dataAttributes={{ cy: 'postalcode-input', testid: 'postalcode-input' }}
      />
      {customMessage || null}
      {hasCustomButton ? (
        <Button
          className={csx(customButtonProps?.styles)}
          disabled={disabled || invalidPostalCode}
          loading={loading}
          type="submit"
          size="normal"
          data-testid="postalcode-submit"
          onClick={(e: any) => onClickAddress(e)}
        >
          <FormattedMessage id={customButtonProps?.title} />
        </Button>
      ) : null}
    </form>
  )
}

PostalCodeInput.defaultProps = {
  initialValue: '',
  disabled: false,
  submitOnBlur: false,
}

// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'FC<PostalCodeInputProps>' is not... Remove this comment to see the full error message
export default injectIntl(withMediaQuery(PostalCodeInput))
